<template>
  <div :class="['snippet', { inline }]">
    <span v-if="label" :id="id + '-label'" class="pb-label">{{ label }}</span>
    <div class="snippet-row">
      <!-- Code is shown as selectable text, not a field: a focused field under 16px makes iOS zoom the page. -->
      <pre v-if="multiline" ref="field" :class="['snippet-field', prose ? 'prose' : 'code pb-mono']" tabindex="0" :aria-labelledby="label ? id + '-label' : null"
           :style="{ maxHeight: (rows * 1.6 + 1.4) + 'em' }">{{ value }}</pre>
      <input v-else ref="field" class="snippet-field" readonly :value="value" :aria-labelledby="label ? id + '-label' : null"
             @focus="$event.target.select()">
      <button type="button" :class="['pb-button', 'sm', copied ? 'tonal' : (secondary ? 'outline' : '')]" :aria-label="copied ? 'Copied' : 'Copy ' + (label || 'text')" @click="copy">
        <i :class="['mdi', copied ? 'mdi-check' : 'mdi-content-copy', 'pb-ico']" aria-hidden="true"></i>{{ copied ? 'Copied' : 'Copy' }}
      </button>
    </div>
    <span class="pb-sr-only" aria-live="polite">{{ copied ? 'Copied to the clipboard' : '' }}</span>
  </div>
</template>

<script>
let counter = 0;

// Selects an element's text when the clipboard API is unavailable (older browsers, http pages).
function selectNode(node) {
  if (node.select) { node.select(); return; }
  const range = document.createRange();
  range.selectNodeContents(node);
  const selection = window.getSelection();
  selection.removeAllRanges();
  selection.addRange(range);
}

export async function copyText(text, fallbackNode) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    if (!fallbackNode) return false;
    selectNode(fallbackNode);
    try { return document.execCommand('copy'); } catch (inner) { return false; }
  }
}

export default {
  name: 'SnippetBox',
  props: {
    value: { type: String, default: '' },
    label: { type: String, default: '' },
    multiline: { type: Boolean, default: false },
    rows: { type: Number, default: 3 },
    inline: { type: Boolean, default: false },
    // Multi-line prose (a message to send), shown in the body face rather than as code.
    prose: { type: Boolean, default: false },
    // An outline Copy button, where another button on the screen is the main action.
    secondary: { type: Boolean, default: false }
  },
  data() { counter += 1; return { id: 'snippet-' + counter, copied: false, timer: null }; },
  beforeDestroy() { window.clearTimeout(this.timer); },
  methods: {
    async copy() {
      await copyText(this.value, this.$refs.field);
      this.copied = true;
      this.$emit('copied');
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => { this.copied = false; }, 2200);
    }
  }
};
</script>

<style scoped>
.snippet-row{display:flex;gap:8px;align-items:stretch}
.snippet-field{flex:1 1 auto;min-width:0;min-height:40px;padding:9px 12px;border:1px solid #D4C9B8;border-radius:10px;background:#FBF8F3;color:#1F2328;
  font:500 14px/1.45 var(--font-body);outline:none}
.snippet-field:focus{border-color:#B4472B;box-shadow:0 0 0 3px rgba(180,71,43,.18)}
.snippet-field.code{margin:0;overflow:auto;font-size:12.5px;line-height:1.6;white-space:pre-wrap;word-break:break-all;user-select:all;-webkit-user-select:all}
.snippet-field.prose{margin:0;overflow:auto;white-space:pre-wrap;font:400 14.5px/1.6 var(--font-body)!important;user-select:all;-webkit-user-select:all}
.snippet-row .pb-button{align-self:flex-start}
@media(max-width:600px){
  .snippet-row{flex-direction:column}
  .snippet-row .pb-button{align-self:stretch}
  input.snippet-field{font-size:16px}
}
</style>
